import * as React from "react"
import { ChevronUp,ChevronDown,Delete,Grid3x3 } from "lucide-react"
import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import { IS_COARSE_POINTER } from "@/lib/touch"
import {
Popover,
PopoverContent,
PopoverTrigger,
} from "@/components/ui/popover"

export interface NumericInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: string | number
  onChange: (value: string) => void
  onStepUp?: () => void
  onStepDown?: () => void
  unit?: string
  allowDecimals?: boolean
  hideStepButtons?: boolean
  prefix?: string
  /** Replaces the up/down steppers with a keypad button that opens an on-screen number pad. */
  keypad?: boolean
}

const KEYPAD_ROWS: string[][] = [
  ['7', '8', '9'],
  ['4', '5', '6'],
  ['1', '2', '3'],
  ['.', '0', 'back'],
]

const NumericInput = React.forwardRef<HTMLInputElement, NumericInputProps>(
  ({ className, value, onChange, onStepUp, onStepDown, unit, allowDecimals = true, hideStepButtons = false, prefix, keypad = false, disabled, ...props }, ref) => {
    const [keypadOpen, setKeypadOpen] = React.useState(false)

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let cleaned = e.target.value.replace(allowDecimals ? /[^0-9.]/g : /[^0-9]/g, '');

      if (allowDecimals) {
        const parts = cleaned.split('.');
        if (parts.length > 2) {
          cleaned = parts[0] + '.' + parts.slice(1).join('');
        }
      }

      onChange(cleaned);
    };

    const handleStepUp = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (disabled) return;
      if (onStepUp) {
        onStepUp();
      } else {
        const cur = parseFloat(String(value || "0")) || 0;
        onChange(String(cur + 1));
      }
    };

    const handleStepDown = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (disabled) return;
      if (onStepDown) {
        onStepDown();
      } else {
        const cur = parseFloat(String(value || "0")) || 0;
        onChange(String(Math.max(0, cur - 1)));
      }
    };

    const pressKey = (key: string) => {
      const cur = String(value ?? '')
      if (key === 'back') {
        onChange(cur.slice(0, -1))
        return
      }
      if (key === '.') {
        if (!allowDecimals || cur.includes('.')) return
        onChange(cur === '' ? '0.' : cur + '.')
        return
      }
      const next = cur + key
      // Money-style inputs: don't let the pad type past 2 decimal places.
      const decimals = next.split('.')[1]
      if (decimals && decimals.length > 2) return
      onChange(next)
    }

    const showSteppers = !hideStepButtons && !keypad

    return (
      <div className={cn(
        "flex rounded-md border bg-background transition-all shadow-none h-9 overflow-hidden",
        "focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]",
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}>
        <div className="relative flex-grow flex items-center h-full">
          {prefix && (
            <span className="pl-3 text-muted-foreground text-sm flex-shrink-0 pointer-events-none">
              {prefix}
            </span>
          )}
          <Input
            {...props}
            ref={ref}
            type="text"
            // On touch-first devices the OS keyboard would cover the checkout —
            // suppress it for keypad inputs and pop the on-screen pad instead.
            inputMode={keypad && IS_COARSE_POINTER ? "none" : allowDecimals ? "decimal" : "numeric"}
            onFocus={(e) => {
              if (keypad && IS_COARSE_POINTER) setKeypadOpen(true)
              props.onFocus?.(e)
            }}
            onClick={(e) => {
              if (keypad && IS_COARSE_POINTER) setKeypadOpen(true)
              props.onClick?.(e)
            }}
            value={value}
            onChange={handleChange}
            disabled={disabled}
            className={cn(
              "border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 relative z-0 min-w-0 h-full bg-transparent no-spinner px-3",
              prefix && "pl-1",
              // Reserve room on the right for the unit label and/or the stepper
              // buttons so the value text never runs underneath either of them.
              unit && (showSteppers || keypad) && "pr-16",
              unit && !showSteppers && !keypad && "pr-10",
              !unit && (showSteppers || keypad) && "pr-8"
            )}
          />
          {showSteppers && (
            <div className="absolute right-0 flex flex-col border-l h-full shrink-0 z-10 bg-background">
              <button
                type="button"
                tabIndex={-1}
                disabled={disabled}
                className="flex-1 flex items-center justify-center px-2 hover:bg-muted border-b transition-colors"
                onClick={handleStepUp}
              >
                <ChevronUp className="h-3 w-3 text-muted-foreground" />
              </button>
              <button
                type="button"
                tabIndex={-1}
                disabled={disabled}
                className="flex-1 flex items-center justify-center px-3 hover:bg-muted transition-colors"
                onClick={handleStepDown}
              >
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
              </button>
            </div>
          )}
          {keypad && (
            <Popover open={keypadOpen} onOpenChange={setKeypadOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  tabIndex={-1}
                  disabled={disabled}
                  aria-label="Open keypad"
                  className="absolute right-0 flex items-center justify-center border-l h-full px-3 shrink-0 z-10 bg-background hover:bg-muted transition-colors"
                >
                  <Grid3x3 className="h-4 w-4 text-muted-foreground" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                sideOffset={6}
                className="w-auto p-2"
                // Keep focus (and the cursor) in the input while tapping keys.
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <div className="grid grid-cols-3 gap-1.5">
                  {KEYPAD_ROWS.flat().map((key) => (
                    <button
                      key={key}
                      type="button"
                      disabled={key === '.' && !allowDecimals}
                      className="flex h-12 w-14 items-center justify-center rounded-lg border bg-background text-lg font-medium tabular-nums transition-colors hover:bg-muted active:bg-muted/80 disabled:opacity-40"
                      onPointerDown={(e) => e.preventDefault()}
                      onClick={() => pressKey(key)}
                      aria-label={key === 'back' ? 'Backspace' : key}
                    >
                      {key === 'back' ? <Delete className="h-5 w-5" /> : key}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="col-span-3 flex h-10 items-center justify-center rounded-lg border bg-background text-sm font-medium text-muted-foreground transition-colors hover:bg-muted active:bg-muted/80"
                    onPointerDown={(e) => e.preventDefault()}
                    onClick={() => onChange('')}
                  >
                    Clear
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          )}
          {unit && (
            <span className={cn(
              "absolute text-[10px] text-muted-foreground pointer-events-none",
              (showSteppers || keypad) ? "right-11" : "right-3"
            )}>
              {unit}
            </span>
          )}
        </div>
      </div>
    )
  }
)

NumericInput.displayName = "NumericInput"

export { NumericInput }
